import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useOrgFleet } from "providers/OrgFleetProvider";
import { getMissions } from "lib/fleetMissionStorage/FleetMissionStorageClient";
import type { OrgMission } from "lib/orgServer/OrgServerClient";
import {
    createScheduledJob,
    deleteScheduledJob,
    listScheduledJobLogsForRange,
    listScheduledJobs,
    ScheduledJob,
    ScheduledJobInput,
    ScheduledJobLog,
    subscribeSchedulePageChanges,
    updateScheduledJob,
} from "lib/fleetMissionStorage/FleetScheduleClient";
import ScheduleEventRenderer from "components/schedules/ScheduleEventRenderer";
import { occurrencesInRange } from "components/schedules/scheduleOccurrences";
import MissionSubpage from "components/Missions/MissionSubpage";
import {
    DEFAULT_SCHEDULE_TIMEZONE,
    scheduleTimezoneLocalToIso,
    scheduleTimezoneOffset,
    scheduleTimezoneOptions,
    scheduleTimezoneToLocalInput,
} from "components/schedules/timezones";
import AnimatedModal from "components/layout/motion/AnimatedModal";

const inputClass = "rounded border border-black70 bg-black90 px-2 py-1 text-sm text-white";

const monthStart = (value: Date) => new Date(value.getFullYear(), value.getMonth(), 1);
const MIN_CALENDAR_YEAR = 2026;
const maxCalendarYear = () => Math.max(MIN_CALENDAR_YEAR, new Date().getFullYear() + 2);
const monthNames = Array.from({ length: 12 }, (_, index) => new Date(2020, index, 1).toLocaleString(undefined, { month: "long" }));
const monthDays = (value: Date) => {
    const first = monthStart(value);
    const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const leading = first.getDay();
    return [...Array(leading).fill(null), ...Array.from({ length: count }, (_, i) => new Date(first.getFullYear(), first.getMonth(), i + 1))];
};

const toLocalInput = (iso: string | null | undefined, timezone: string) => scheduleTimezoneToLocalInput(iso, timezone);
const toLocalDateInput = (date: Date | null | undefined, timezone: string) => {
    if (!date) return "";
    const nextMinute = scheduleTimezoneToLocalInput(new Date(Date.now() + 60_000).toISOString(), timezone);
    const selectedDate = new Date(date);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}T${nextMinute.slice(11, 16)}`;
};

const toIso = (value: string, timezone: string) => scheduleTimezoneLocalToIso(value, timezone);

function YearSelector({ year, onChange }: { year: number; onChange: (year: number) => void }) {
    const listRef = useRef<HTMLDivElement>(null);
    const years = Array.from(
        { length: Math.max(1, maxCalendarYear() - MIN_CALENDAR_YEAR + 1) },
        (_, index) => MIN_CALENDAR_YEAR + index,
    );

    useEffect(() => {
        const selected = listRef.current?.querySelector<HTMLElement>(`[data-year="${year}"]`);
        selected?.scrollIntoView({ block: "center" });
    }, [year]);

    return <div>
        <p className="mb-1 text-xs uppercase tracking-wide text-black40">Year</p>
        <div ref={listRef} className="max-h-40 space-y-1 overflow-y-scroll rounded border border-black70 bg-black100 p-1">
            {years.map((option) => <button
                key={option}
                type="button"
                data-year={option}
                className={`block w-full rounded px-2 py-1 text-left text-h5 ${option === year ? "bg-primary2 text-white" : "text-black40 hover:bg-black80 hover:text-white"}`}
                onClick={() => onChange(option)}
            >{option}</button>)}
        </div>
    </div>;
}

function JobEditor({
    job,
    missions,
    onSaved,
    onCancel,
    initialStartsAt,
    onBack,
    showMissionPreview = false,
}: {
    job: ScheduledJob | null;
    missions: OrgMission[];
    onSaved: (job: ScheduledJob) => void;
    onCancel?: (job: ScheduledJob) => Promise<void>;
    initialStartsAt?: Date | null;
    onBack?: () => void;
    showMissionPreview?: boolean;
}) {
    const { proxyHostname } = useOrgFleet();
    const [missionKey, setMissionKey] = useState(job ? `${job.arcSerial}\u0000${job.missionId}` : "");
    const [missionPickerOpen, setMissionPickerOpen] = useState(true);
    const [missionSearch, setMissionSearch] = useState("");
    const [arcFilter, setArcFilter] = useState("");
    const [timezone, setTimezone] = useState(job?.timezone ?? DEFAULT_SCHEDULE_TIMEZONE);
    const [startsAt, setStartsAt] = useState(job ? toLocalInput(job.startsAt, job.timezone) : toLocalDateInput(initialStartsAt, job?.timezone ?? DEFAULT_SCHEDULE_TIMEZONE));
    const [repeat, setRepeat] = useState(job?.repeat ?? false);
    const [repeatInterval, setRepeatInterval] = useState(job?.repeatInterval ?? 1);
    const [repeatUnit, setRepeatUnit] = useState<ScheduledJob["repeatUnit"]>(job?.repeatUnit ?? "DAY");
    const [timezoneSearch, setTimezoneSearch] = useState("");
    const [timezoneSearchFocused, setTimezoneSearchFocused] = useState(false);
    const timezoneListRef = useRef<HTMLDivElement>(null);
    const [repeatUntil, setRepeatUntil] = useState(toLocalInput(job?.repeatUntil, job?.timezone ?? DEFAULT_SCHEDULE_TIMEZONE));
    const [maxDelaySeconds, setMaxDelaySeconds] = useState(job?.maxDelaySeconds ?? 900);
    const [enabled, setEnabled] = useState(job?.enabled ?? true);
    const [error, setError] = useState<string | null>(null);
    const [canceling, setCanceling] = useState(false);

    useEffect(() => {
        setMissionKey(job ? `${job.arcSerial}\u0000${job.missionId}` : "");
        setMissionPickerOpen(true);
        setMissionSearch("");
        setArcFilter("");
        const nextTimezone = job?.timezone ?? DEFAULT_SCHEDULE_TIMEZONE;
        setStartsAt(job ? toLocalInput(job.startsAt, nextTimezone) : toLocalDateInput(initialStartsAt, nextTimezone));
        setRepeat(job?.repeat ?? false);
        setRepeatInterval(job?.repeatInterval ?? 1);
        setRepeatUnit(job?.repeatUnit ?? "DAY");
        setTimezone(nextTimezone);
        setTimezoneSearch("");
        setTimezoneSearchFocused(false);
        setRepeatUntil(toLocalInput(job?.repeatUntil, nextTimezone));
        setMaxDelaySeconds(job?.maxDelaySeconds ?? 900);
        setEnabled(job?.enabled ?? true);
        setError(null);
        setCanceling(false);
    }, [job, initialStartsAt]);

    const selected = missions.find((mission) => `${mission.sourceArcSerial}\u0000${mission.sourceMissionId}` === missionKey);
    useEffect(() => {
        if (showMissionPreview && selected) setMissionPickerOpen(false);
    }, [selected, showMissionPreview]);
    const arcSerials = [...new Set(missions.map((mission) => mission.sourceArcSerial))].sort((a, b) => a.localeCompare(b));
    const normalizedMissionSearch = missionSearch.trim().toLocaleLowerCase();
    const filteredMissions = missions
        .filter((mission) => !arcFilter || mission.sourceArcSerial === arcFilter)
        .filter((mission) => !normalizedMissionSearch
            || mission.name.toLocaleLowerCase().includes(normalizedMissionSearch)
            || mission.sourceMissionId.toLocaleLowerCase().includes(normalizedMissionSearch))
        .sort((a, b) => a.name.localeCompare(b.name) || a.sourceArcSerial.localeCompare(b.sourceArcSerial));
    const normalizedTimezoneSearch = timezoneSearch.trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase();
    const timezoneOptions = scheduleTimezoneOptions.some((option) => option.value === timezone)
        ? scheduleTimezoneOptions
        : [{ value: timezone, cities: [timezone], searchText: timezone.toLocaleLowerCase() }, ...scheduleTimezoneOptions];
    const filteredTimezoneOptions = timezoneOptions.filter((option) => !normalizedTimezoneSearch || option.searchText.includes(normalizedTimezoneSearch));
    const selectedTimezone = timezoneOptions.find((option) => option.value === timezone);
    const timezoneInputValue = timezoneSearchFocused || timezoneSearch
        ? timezoneSearch
        : selectedTimezone?.value ?? timezone;
    useEffect(() => {
        const selectedOption = Array.from(timezoneListRef.current?.querySelectorAll<HTMLElement>("[data-timezone]") ?? [])
            .find((option) => option.dataset.timezone === timezone);
        selectedOption?.scrollIntoView({ block: "nearest" });
    }, [timezone, timezoneSearch]);
    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!proxyHostname || !selected || !startsAt) {
            setError("Choose a live mission and a start time.");
            return;
        }
        const startsAtIso = toIso(startsAt, timezone);
        const repeatUntilIso = repeatUntil ? toIso(repeatUntil, timezone) : null;
        if (!startsAtIso || (repeatUntil && !repeatUntilIso)) {
            setError("The selected local time does not exist in this timezone.");
            return;
        }
        if (repeat && (!Number.isInteger(repeatInterval) || repeatInterval <= 0)) {
            setError("Repeat interval must be a positive whole number.");
            return;
        }
        if (repeat && repeatUntilIso && new Date(repeatUntilIso) <= new Date(startsAtIso)) {
            setError("Repeat until must be later than the first run.");
            return;
        }
        if (!Number.isInteger(maxDelaySeconds) || maxDelaySeconds <= 0) {
            setError("Maximum delay must be a positive whole number of seconds.");
            return;
        }
        const input: ScheduledJobInput = {
            missionId: selected.sourceMissionId,
            arcSerial: selected.sourceArcSerial,
            startsAt: startsAtIso,
            repeat,
            repeatInterval,
            repeatUnit,
            repeatUntil: repeat ? repeatUntilIso : null,
            timezone,
            maxDelaySeconds,
            enabled,
        };
        try {
            const saved = job
                ? await updateScheduledJob(proxyHostname, job.id, input)
                : await createScheduledJob(proxyHostname, input);
            onSaved(saved);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Schedule could not be saved.");
        }
    };

    const cancel = async () => {
        if (!job || !onCancel || canceling) return;
        if (!window.confirm("Cancel this schedule? Future occurrences will no longer run.")) return;
        setCanceling(true);
        try {
            await onCancel(job);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Schedule could not be canceled.");
            setCanceling(false);
        }
    };

    return (
        <div className={showMissionPreview ? "grid min-h-0 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.9fr)]" : ""}>
            <form onSubmit={submit} className="flex min-w-0 flex-col gap-2 rounded-r10 bg-black90 p-3">
            <div className="flex items-center gap-2">
                {job && onBack && <button type="button" className="px-1 text-2xl leading-none text-white hover:text-primary2" aria-label="Back to scheduled jobs" title="Back to scheduled jobs" onClick={onBack}>‹</button>}
                <h2 className="text-h3">{job ? "Edit schedule" : "New schedule"}</h2>
            </div>
            {job && <div className="rounded border border-black70 bg-black100 px-2 py-1 text-sm text-black40">
                <div>Mission: <span className="text-white">{missions.find((mission) => mission.sourceArcSerial === job.arcSerial && mission.sourceMissionId === job.missionId)?.name ?? job.missionId}</span></div>
                <div>ARC: <span className="text-white">{job.arcSerial}</span></div>
            </div>}
            <div className="text-sm text-black40">
                <div className="flex items-center justify-between gap-2">
                    <div>Mission</div>
                    {showMissionPreview && <button
                        type="button"
                        className="rounded px-2 py-1 text-xs text-primary2 hover:bg-black80 xl:hidden"
                        onClick={() => setMissionPickerOpen((open) => !open)}
                    >{missionPickerOpen ? "Hide mission list" : "Change mission"}</button>}
                </div>
                {showMissionPreview && <div className="mt-1 flex items-center justify-between gap-2 rounded border border-black70 bg-black100 px-3 py-2 xl:hidden">
                    {selected ? <div className="min-w-0">
                        <span className="block truncate text-sm text-white">{selected.name}</span>
                        <span className="block truncate text-xs text-black40">{selected.sourceArcSerial} · {selected.sourceMissionId}</span>
                    </div> : <span className="text-sm text-black40">No mission selected.</span>}
                </div>}
                <div className={showMissionPreview && !missionPickerOpen ? "hidden xl:block" : "block"}>
                    <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(9rem,0.65fr)]">
                        <input
                            className={inputClass + " w-full"}
                            type="search"
                            placeholder="Search mission name or ID"
                            aria-label="Search missions"
                            value={missionSearch}
                            onChange={(event) => setMissionSearch(event.target.value)}
                        />
                        <select className={inputClass + " w-full"} value={arcFilter} onChange={(event) => setArcFilter(event.target.value)} aria-label="Filter missions by ARC serial">
                            <option value="">All ARCs</option>
                            {arcSerials.map((serial) => <option key={serial} value={serial}>{serial}</option>)}
                        </select>
                    </div>
                    <div role="listbox" aria-label="Available missions" className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded border border-black70 bg-black100 p-1">
                        {filteredMissions.length === 0 && <p className="p-2 text-sm text-black40">No missions match these filters.</p>}
                        {filteredMissions.map((mission) => {
                            const key = `${mission.sourceArcSerial}\u0000${mission.sourceMissionId}`;
                            const isSelected = key === missionKey;
                            return <button
                                key={key}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`block w-full rounded px-3 py-2 text-left text-sm ${isSelected ? "bg-primary2 text-white" : "text-white hover:bg-black80"}`}
                                onClick={() => setMissionKey(key)}
                            >
                                <span className="block truncate">{mission.name}</span>
                                <span className={`block truncate text-xs ${isSelected ? "text-white/80" : "text-black40"}`}>{mission.sourceArcSerial} · {mission.sourceMissionId}</span>
                            </button>;
                        })}
                    </div>
                </div>
                {!selected && <p className="mt-1 text-xs text-warning">Select a mission to continue.</p>}
            </div>
            <label className="text-sm text-black40">Starts at
                <input className={inputClass + " mt-1 w-full"} type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} required />
            </label>
            <div className="grid grid-cols-2 gap-2">
                <label className="text-sm text-black40">Repeat every
                    <input className={inputClass + " mt-1 w-full"} type="number" min={1} step={1} value={repeatInterval} onChange={(event) => setRepeatInterval(Number(event.target.value))} disabled={!repeat} />
                </label>
                <label className="text-sm text-black40">Unit
                    <select className={inputClass + " mt-1 w-full"} value={repeatUnit} onChange={(event) => setRepeatUnit(event.target.value as ScheduledJob["repeatUnit"])} disabled={!repeat}>
                        <option value="MINUTE">Minute</option><option value="HOUR">Hour</option><option value="DAY">Day</option><option value="WEEK">Week</option><option value="MONTH">Month</option>
                    </select>
                </label>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={repeat} onChange={(event) => setRepeat(event.target.checked)} /> Repeating</label>
            <label className="text-sm text-black40">Repeat until (optional)
                <input className={inputClass + " mt-1 w-full"} type="datetime-local" min={startsAt || undefined} value={repeatUntil} onChange={(event) => setRepeatUntil(event.target.value)} disabled={!repeat} />
            </label>
            <div className="text-sm text-black40">
                <div>IANA timezone</div>
                <input
                    className={inputClass + " mt-1 w-full"}
                    type="search"
                    placeholder="Search city or timezone"
                    aria-label="Search IANA timezones"
                    autoComplete="off"
                    value={timezoneInputValue}
                    onFocus={() => { setTimezoneSearchFocused(true); setTimezoneSearch(""); }}
                    onBlur={() => { setTimezoneSearchFocused(false); setTimezoneSearch(""); }}
                    onChange={(event) => setTimezoneSearch(event.target.value)}
                />
                {selectedTimezone && <div className="mt-2 rounded border border-primary2/50 bg-primary2/10 px-3 py-2 text-sm text-white">
                    <span className="mr-1 text-black40">Selected:</span>
                    {selectedTimezone.cities.join(", ")} ({scheduleTimezoneOffset(selectedTimezone.value)})
                </div>}
                <div ref={timezoneListRef} role="listbox" aria-label="Available IANA timezones" className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded border border-black70 bg-black100 p-1">
                    {filteredTimezoneOptions.length === 0 && <p className="p-2 text-sm text-black40">No timezones match this search.</p>}
                    {filteredTimezoneOptions.map((option) => {
                        const isSelected = option.value === timezone;
                        return <button
                            key={option.value}
                            type="button"
                            role="option"
                            data-timezone={option.value}
                            aria-selected={isSelected}
                            className={`block w-full rounded px-3 py-2 text-left text-sm ${isSelected ? "bg-primary2 text-white" : "text-white hover:bg-black80"}`}
                            onPointerDown={(event) => event.preventDefault()}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => { setTimezone(option.value); setTimezoneSearch(""); setTimezoneSearchFocused(false); }}
                        >
                            <span className="block truncate">{option.cities.join(", ")} ({scheduleTimezoneOffset(option.value)})</span>
                            <span className={`block truncate text-xs ${isSelected ? "text-white/80" : "text-black40"}`}>{option.value}</span>
                        </button>;
                    })}
                </div>
            </div>
            <label className="text-sm text-black40">Maximum delay (seconds)
                <input className={inputClass + " mt-1 w-full"} type="number" min={1} step={1} value={maxDelaySeconds} onChange={(event) => setMaxDelaySeconds(Number(event.target.value))} />
            </label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> Enabled</label>
            {error && <p className="text-sm text-warning">{error}</p>}
            <div className="mt-1 flex justify-end gap-2">
                {job && <button className="rounded border border-warning px-2 py-1 text-sm text-warning disabled:opacity-50" type="button" onClick={() => void cancel()} disabled={canceling}>
                    {canceling ? "Canceling…" : "Cancel schedule"}
                </button>}
                <button className="rounded bg-primary2 px-2 py-1 text-sm text-white" type="submit">{job ? "Save" : "Create"}</button>
            </div>
            </form>
            {showMissionPreview && selected && <aside className="relative h-[min(50vh,32rem)] max-h-[32rem] min-h-0 min-w-0 overflow-hidden">
                <button
                    type="button"
                    className="absolute right-2 top-2 z-10 rounded px-2 text-2xl leading-none text-black40 hover:bg-black80 hover:text-white"
                    aria-label="Close mission preview"
                    title="Close mission preview"
                    onClick={() => { setMissionKey(""); setMissionPickerOpen(true); }}
                >×</button>
                <MissionSubpage
                    missionId={selected.sourceMissionId}
                    sourceArcSerial={selected.sourceArcSerial}
                    readOnly
                    showBackButton={false}
                    onBack={() => undefined}
                />
            </aside>}
        </div>
    );
}

const statusClass = (status: string) => status === "TRIGGERED" ? "text-green-400" : status === "RUNNING" || status === "DISPATCHING" ? "text-yellow-300" : "text-warning";
type SchedulePanelTab = "scheduled" | "history";

export default function SchedulesPage() {
    const { proxyHostname } = useOrgFleet();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<ScheduledJob[]>([]);
    const [missions, setMissions] = useState<OrgMission[]>([]);
    const [selected, setSelected] = useState<ScheduledJob | null>(null);
    const [logs, setLogs] = useState<ScheduledJobLog[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsError, setLogsError] = useState<string | null>(null);
    const [selectedOccurrence, setSelectedOccurrence] = useState<string | null>(null);
    const [panelTab, setPanelTab] = useState<SchedulePanelTab>("scheduled");
    const [panelOpen, setPanelOpen] = useState(false);
    const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
    const [newScheduleOpen, setNewScheduleOpen] = useState(false);
    const [month, setMonth] = useState(() => monthStart(new Date()));
    const [calendarPickerOpen, setCalendarPickerOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const calendarPickerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!calendarPickerOpen) return undefined;
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!calendarPickerRef.current?.contains(event.target as Node)) setCalendarPickerOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, [calendarPickerOpen]);

    const days = useMemo(() => monthDays(month), [month]);
    const visibleOccurrences = useMemo(() => {
        const rangeStart = monthStart(month);
        const rangeEnd = new Date(month.getFullYear(), month.getMonth() + 1, 1);
        return jobs.flatMap((job) => occurrencesInRange(job, rangeStart, rangeEnd)
            .map((occurrenceAt) => ({ job, occurrenceAt })));
    }, [jobs, month]);
    // `nextRunAt` is mutable execution state. Expansion always starts from
    // `startsAt`, so dispatching a series cannot rewrite calendar history.
    const dayEvents = (day: Date) => visibleOccurrences.filter(({ occurrenceAt }) => {
        const occurrence = new Date(occurrenceAt);
        return occurrence.getFullYear() === day.getFullYear()
            && occurrence.getMonth() === day.getMonth()
            && occurrence.getDate() === day.getDate();
    });
    const scheduledForDate = useMemo(
        () => scheduledDate ? dayEvents(scheduledDate) : [],
        [scheduledDate, visibleOccurrences],
    );

    const reloadJobsAndMissions = useCallback(() => {
        if (!proxyHostname) return;
        Promise.all([listScheduledJobs(proxyHostname), getMissions(proxyHostname)])
            .then(([nextJobs, nextMissions]) => {
                setJobs(nextJobs);
                setMissions(nextMissions);
                setSelected((current) => current
                    ? nextJobs.find((job) => job.id === current.id) ?? null
                    : null);
            })
            .catch((err) => setError(err instanceof Error ? err.message : "Unable to load schedules."));
    }, [proxyHostname]);
    const reloadLogs = useCallback(() => {
        if (!proxyHostname || !scheduledDate || scheduledForDate.length === 0) {
            setLogs([]);
            setLogsError(null);
            return;
        }
        setLogsLoading(true);
        setLogsError(null);
        const dayStart = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
        const dayEnd = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate() + 1);
        listScheduledJobLogsForRange(proxyHostname, dayStart.toISOString(), dayEnd.toISOString())
            .then((nextLogs) => {
                nextLogs.sort((a, b) => new Date(b.occurrenceAt).getTime() - new Date(a.occurrenceAt).getTime());
                setLogs(nextLogs);
            })
            .catch(() => { setLogs([]); setLogsError("Unable to load execution history."); })
            .finally(() => setLogsLoading(false));
    }, [proxyHostname, scheduledDate, scheduledForDate]);

    useEffect(reloadJobsAndMissions, [reloadJobsAndMissions]);
    useEffect(reloadLogs, [reloadLogs]);
    useEffect(() => subscribeSchedulePageChanges(
        proxyHostname,
        selected?.id ?? null,
        (entities) => {
            if (entities.includes("missions") || entities.includes("scheduled_jobs")) {
                reloadJobsAndMissions();
            }
            if (entities.includes("scheduled_job_logs")) reloadLogs();
        },
        (err) => console.warn("Schedules live update disconnected", err),
    ), [proxyHostname, selected?.id, reloadJobsAndMissions, reloadLogs]);

    const onSaved = (job: ScheduledJob) => {
        setJobs((current) => current.some((item) => item.id === job.id) ? current.map((item) => item.id === job.id ? job : item) : [...current, job]);
        setSelected(job);
        setPanelTab("scheduled");
        setPanelOpen(true);
        setNewScheduleOpen(false);
    };
    const onCancel = async (job: ScheduledJob) => {
        if (!proxyHostname || !await deleteScheduledJob(proxyHostname, job.id)) {
            throw new Error("Schedule could not be canceled.");
        }
        setJobs((current) => current.filter((item) => item.id !== job.id));
        setSelected(null);
        setSelectedOccurrence(null);
        setLogs([]);
        setPanelTab("scheduled");
        setPanelOpen(false);
    };
    const selectDate = (day: Date) => {
        setScheduledDate(day);
        setSelected(null);
        setSelectedOccurrence(null);
        setPanelTab("scheduled");
        setPanelOpen(true);
    };
    const selectScheduledJob = (job: ScheduledJob, occurrenceAt: string | null = null) => {
        if (occurrenceAt) setScheduledDate(new Date(occurrenceAt));
        setSelected(job);
        setSelectedOccurrence(occurrenceAt);
        setPanelTab("scheduled");
        setPanelOpen(true);
    };
    const panelVisible = panelOpen && (scheduledDate !== null || selected !== null);
    const changeMonth = (offset: number) => {
        const next = new Date(month.getFullYear(), month.getMonth() + offset, 1);
        if (next.getFullYear() < MIN_CALENDAR_YEAR || next.getFullYear() > maxCalendarYear()) return;
        setMonth(next);
    };
    const goToToday = () => {
        const today = new Date();
        setMonth(monthStart(today));
        selectDate(today);
    };

    return (
        <div className="page-with-top-bar flex h-full min-h-0 flex-col gap-3 overflow-y-auto p-6">
            <div className="flex items-center justify-between"><h1 className="text-h2">Schedules</h1><div className="flex items-center gap-3">{selected && !panelOpen && <button type="button" className="text-h5 text-primary2" onClick={() => setPanelOpen(true)}>Show selected</button>}<button type="button" className="text-h5 text-primary2" onClick={() => navigate("/fleet/dashboard")}>Back to dashboard</button></div></div>
            {error && <p className="text-h5 text-warning">{error}</p>}
            <div className={`flex min-h-0 flex-1 gap-3 ${panelVisible ? "flex-row" : "flex-col"}`}>
                <section className={`relative min-w-0 rounded-r10 bg-black90 p-4 ${panelVisible ? "flex-1" : "w-full"}`}>
                    <div className="relative mb-3 flex items-center justify-between">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <button type="button" className="px-1 text-3xl leading-none" aria-label="Previous month" onClick={() => changeMonth(-1)}>‹</button>
                            <button type="button" className="min-w-0 flex-1 rounded px-2 py-1 text-h3 text-white hover:bg-black80" onClick={() => setCalendarPickerOpen((open) => !open)} aria-expanded={calendarPickerOpen}>
                                {month.toLocaleString(undefined, { month: "long", year: "numeric" })}
                            </button>
                            <button type="button" className="px-1 text-3xl leading-none" aria-label="Next month" onClick={() => changeMonth(1)}>›</button>
                        </div>
                        <button type="button" className="rounded border border-black70 px-2 py-1 text-xs text-primary2 hover:border-primary2" onClick={goToToday}>Today</button>
                        {calendarPickerOpen && <div ref={calendarPickerRef} className="absolute left-1/2 top-full z-20 mt-2 w-[min(28rem,calc(100vw-3rem))] -translate-x-1/2 rounded-r10 border border-black70 bg-black90 p-3 shadow-xl">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="mb-1 text-xs uppercase tracking-wide text-black40">Month</p>
                                    <div className="grid grid-cols-2 gap-1 rounded border border-black70 bg-black100 p-1">
                                        {monthNames.map((name, index) => <button
                                            key={name}
                                            type="button"
                                            className={`rounded px-2 py-1 text-left text-h5 ${index === month.getMonth() ? "bg-primary2 text-white" : "text-black40 hover:bg-black80 hover:text-white"}`}
                                            onClick={() => { setMonth(new Date(month.getFullYear(), index, 1)); setCalendarPickerOpen(false); }}
                                        >{name}</button>)}
                                    </div>
                                </div>
                                <YearSelector year={month.getFullYear()} onChange={(year) => setMonth(new Date(year, month.getMonth(), 1))} />
                            </div>
                        </div>}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-h5 text-black40">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <div key={day}>{day}</div>)}
                        {days.map((day, index) => {
                            if (!day) return <div key={`blank-${index}`} />;
                            const events = dayEvents(day);
                            const orderedEvents = [...events].sort((a, b) => new Date(a.occurrenceAt).getTime() - new Date(b.occurrenceAt).getTime());
                            const precedingCount = Math.max(0, orderedEvents.length - 3);
                            const displayedEvents = precedingCount > 0 ? orderedEvents.slice(-3) : orderedEvents;
                            const isSelectedDate = scheduledDate !== null
                                && day.getFullYear() === scheduledDate.getFullYear()
                                && day.getMonth() === scheduledDate.getMonth()
                                && day.getDate() === scheduledDate.getDate();
                            return <div key={day.toISOString()} role="button" tabIndex={0} className={`min-h-[5.5rem] rounded border p-1 text-left hover:border-primary2 ${isSelectedDate ? "border-primary2 bg-primary2/10" : "border-black70"}`} onClick={() => selectDate(day)} onKeyDown={(event) => { if (event.key === "Enter") selectDate(day); }}>
                                <span className="text-h5">{day.getDate()}</span>
                                {precedingCount > 0 && <span className="mt-1 block truncate px-1 text-xs text-black40">{precedingCount} more...</span>}
                                {displayedEvents.map(({ job, occurrenceAt }) => {
                                    const mission = missions.find((item) => item.sourceArcSerial === job.arcSerial && item.sourceMissionId === job.missionId);
                                    return <span key={`${job.id}:${occurrenceAt}`} className={`mt-1 block truncate rounded px-1 text-xs ${job.enabled ? "bg-primary2/40" : "bg-black70 text-black40 line-through"}`}><ScheduleEventRenderer event={{ id: job.id, title: `${mission?.name ?? job.missionId} - ${job.arcSerial}`, occurrenceAt, disabled: !job.enabled }} onOpenLog={(event) => { selectScheduledJob(job, event.occurrenceAt); setPanelTab("history"); }} /></span>;
                                })}
                            </div>;
                        })}
                    </div>
                </section>
                {panelVisible && <aside className="flex min-h-0 w-[clamp(200px,30vw,400px)] min-w-[200px] max-w-[400px] shrink-0 flex-col overflow-hidden rounded-r10 border border-black70 bg-black90 shadow-lg">
                    <div className="flex border-b border-black70">
                        <button type="button" className="px-2 py-2 text-h4 text-black40 hover:text-white" aria-label="Close schedule panel" title="Close schedule panel" onClick={() => setPanelOpen(false)}>×</button>
                        <button type="button" className={`flex-1 px-2 py-2 text-xs ${panelTab === "scheduled" ? "border-b-2 border-primary2 text-white" : "text-black40"}`} onClick={() => setPanelTab("scheduled")}>Scheduled</button>
                        <button type="button" disabled={!scheduledDate} className={`flex-1 px-2 py-2 text-xs ${panelTab === "history" ? "border-b-2 border-primary2 text-white" : "text-black40"} disabled:cursor-not-allowed disabled:opacity-40`} onClick={() => scheduledDate && setPanelTab("history")}>Execution history</button>
                    </div>
                    <div className="min-h-0 overflow-y-auto p-2">
                        {panelTab === "scheduled" && !selected && <section className="rounded-r10 bg-black90 p-2">
                            <div className="mb-2 flex items-center justify-between gap-2"><h2 className="text-h3">Scheduled</h2><button type="button" className="rounded bg-primary2 px-2 py-1 text-xs text-white" onClick={() => setNewScheduleOpen(true)}>+ New schedule</button></div>
                            {scheduledDate && <p className="mb-2 text-xs text-black40">{scheduledDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>}
                            {scheduledForDate.length === 0 && <p className="text-h5 text-black40">No scheduled jobs for this date.</p>}
                            <div className="space-y-2">{scheduledForDate.map(({ job, occurrenceAt }) => <button key={`${job.id}:${occurrenceAt}`} type="button" className="w-full rounded border border-black70 bg-black100 p-2 text-left hover:border-primary2" onClick={() => selectScheduledJob(job, occurrenceAt)}>
                                <div className="flex items-start justify-between gap-2"><span className="truncate text-h5 text-white">{missions.find((mission) => mission.sourceArcSerial === job.arcSerial && mission.sourceMissionId === job.missionId)?.name ?? job.missionId}</span><span className="shrink-0 text-xs text-black40">{new Date(occurrenceAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span></div>
                                <div className="text-xs text-black40">{job.arcSerial} · {job.enabled ? "Enabled" : "Disabled"}</div>
                            </button>)}</div>
                        </section>}
                        {panelTab === "scheduled" && selected && <JobEditor job={selected} missions={missions} onSaved={onSaved} onCancel={onCancel} onBack={() => { setSelected(null); setSelectedOccurrence(null); }} />}
                        {panelTab === "history" && (scheduledDate ? <section className="rounded-r10 bg-black90 p-2">
                            <div className="mb-2"><h2 className="text-h3">Execution history</h2><p className="text-xs text-black40">{scheduledDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p></div>
                            {logsLoading && <p className="text-h5 text-black40">Loading execution history…</p>}
                            {logsError && <p className="text-h5 text-warning">{logsError}</p>}
                            {!logsLoading && !logsError && <div className="space-y-2">{logs.length ? logs.map((log) => <div key={log.id} className={`border-b border-black70 pb-2 text-h5 ${selectedOccurrence === log.occurrenceAt ? "border-primary2" : ""}`}><div className="flex justify-between gap-1"><span>{new Date(log.occurrenceAt).toLocaleString()}</span><strong className={statusClass(log.status)}>{log.status}</strong></div><div className="text-black40">{missions.find((mission) => mission.sourceArcSerial === log.arcSerial && mission.sourceMissionId === log.missionId)?.name ?? log.missionId} · {log.arcSerial}</div><div className="text-black40">{log.message || "No message"}</div>{log.sourceRunId && <Link className="mt-1 inline-block text-primary2 hover:underline" to={`/fleet/dashboard/log/mission?arc=${encodeURIComponent(log.arcSerial)}&runId=${encodeURIComponent(log.sourceRunId)}`}>See Run</Link>}</div>) : <p className="text-h5 text-black40">No executions yet.</p>}</div>}
                        </section> : <p className="p-2 text-h5 text-black40">Select a date to view its execution history.</p>)}
                    </div>
                </aside>}
            </div>
            <AnimatedModal visible={newScheduleOpen} onClose={() => setNewScheduleOpen(false)} className="max-h-[90vh] w-full max-w-7xl overflow-y-auto rounded-r10 bg-black100 p-3">
                <JobEditor job={null} missions={missions} onSaved={onSaved} initialStartsAt={scheduledDate} showMissionPreview />
            </AnimatedModal>
        </div>
    );
}
